import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useEffect, useState, useMemo } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Header, VisitorBanner } from '@/components/layout'
import { RecordGameDialog, RegisterInterestDialog } from '@/components/league'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { useGroups } from '@/hooks/useGroups'
import { useProfile } from '@/hooks/useProfile'
import { useDocumentHead } from '@/hooks/useDocumentHead'
import { groupService } from '@/services/groupService'
import { leagueService } from '@/services/leagueService'
import { seasonService } from '@/services/seasonService'
import { bracketTemplateService } from '@/services/bracketTemplateService'
import { bracketService } from '@/services/bracketService'
import { bracketPlayerService } from '@/services/bracketPlayerService'
import { registrationService, type Registration } from '@/services/registrationService'
import type { Group } from '@/services/groupService'
import type { League } from '@/services/leagueService'
import type { Season } from '@/services/seasonService'
import type { BracketTemplate } from '@/services/bracketTemplateService'
import type { SeasonBracket } from '@/services/bracketService'
import type { BracketPlayer } from '@/services/bracketPlayerService'
import { 
  ArrowLeft, AlertCircle, Loader2, Users, Trophy,
  User, UsersRound, Lock, MoreHorizontal, Settings
} from 'lucide-react'

// Import refactored components
import {
  UpcomingSeasonCallout,
  SeasonSelector,
  BracketSelector,
  StandingsTable,
  SeasonInfoCard,
  NoBracketsCard,
} from '@/components/league-view'

export function LeagueView() {
  const { groupId, leagueId } = useParams<{ groupId: string; leagueId: string }>()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { isAuthenticated, isLoading: authLoading, loginWithRedirect } = useAuth0()
  const { groups, isLoading: groupsLoading } = useGroups()
  const { profile } = useProfile()
  
  // State for public group data
  const [publicGroup, setPublicGroup] = useState<Group | null>(null)
  const [publicGroupLoading, setPublicGroupLoading] = useState(false)
  const [publicGroupError, setPublicGroupError] = useState<string | null>(null)
  
  // Data state
  const [league, setLeague] = useState<League | null>(null)
  const [leagueLoading, setLeagueLoading] = useState(true)
  const [leagueError, setLeagueError] = useState<string | null>(null)
  
  // Record game dialog state
  const [showRecordGame, setShowRecordGame] = useState(false)
  
  // Registration dialog state
  const [showRegistrationDialog, setShowRegistrationDialog] = useState(false)
  const [myRegistration, setMyRegistration] = useState<Registration | null>(null)
  const [registrationLoading, setRegistrationLoading] = useState(false)
  const [registrationSubmitting, setRegistrationSubmitting] = useState(false)
  const [registrationError, setRegistrationError] = useState<string | null>(null)
  const [isAlreadyInSeason, setIsAlreadyInSeason] = useState(false)
  
  // Season selector dropdown
  const [seasonDropdownOpen, setSeasonDropdownOpen] = useState(false)
  
  const [seasons, setSeasons] = useState<Season[]>([])
  const [seasonsLoading, setSeasonsLoading] = useState(true)
  
  const [templates, setTemplates] = useState<BracketTemplate[]>([])
  
  const [seasonBrackets, setSeasonBrackets] = useState<Record<string, SeasonBracket[]>>({})
  const [bracketPlayers, setBracketPlayers] = useState<Record<string, BracketPlayer[]>>({})
  const [playersLoading, setPlayersLoading] = useState(false)
  
  // URL state
  const selectedSeasonId = searchParams.get('season')
  const selectedBracketId = searchParams.get('bracket')
  
  // Try to find group from authenticated membership list
  const memberGroup = useMemo(() => groups.find((g: { id: string }) => g.id === groupId), [groups, groupId])
  
  // Use member group if available, otherwise use public group data
  const group = memberGroup || publicGroup
  
  // Determine if we're viewing as a public/non-member user
  const isPublicView = !memberGroup && !!publicGroup
  
  // Only allow admin actions for actual members who are admins
  const canManageGroup = memberGroup?.isAdmin
  
  // Track if user is a member
  const isMember = !!memberGroup

  // Set page title
  useDocumentHead({
    title: league && group ? `${league.name} - ${group.name}` : 'League',
    description: league ? `${league.name} standings and rankings` : undefined,
    image: group?.icon?.startsWith('https://') ? group.icon : undefined,
  })

  // Fetch public group data if user is not a member or not authenticated
  useEffect(() => {
    if (authLoading) return
    if (isAuthenticated && groupsLoading) return
    if (isMember) return
    if (publicGroup?.id === groupId) return
    if (!groupId) return
    
    let cancelled = false
    
    async function fetchPublicGroup() {
      setPublicGroupLoading(true)
      setPublicGroupError(null)
      
      try {
        const data = await groupService.getPublic(groupId!)
        if (!cancelled) {
          setPublicGroup(data)
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load public group:', err)
          setPublicGroupError(err instanceof Error ? err.message : 'Group not found or not public')
        }
      } finally {
        if (!cancelled) {
          setPublicGroupLoading(false)
        }
      }
    }
    
    fetchPublicGroup()
    
    return () => { cancelled = true }
  }, [groupId, authLoading, isAuthenticated, groupsLoading, isMember, publicGroup?.id])
  
  // Derived data
  const sortedSeasons = useMemo(() => {
    return [...seasons].sort((a, b) => {
      if (a.status === 'active' && b.status !== 'active') return -1
      if (b.status === 'active' && a.status !== 'active') return 1
      return new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
    })
  }, [seasons])
  
  const upcomingSeasons = useMemo(() => 
    seasons.filter(s => s.status === 'upcoming').sort((a, b) => 
      new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
    ), [seasons])
  
  const pastSeasons = useMemo(() => 
    seasons.filter(s => s.status === 'completed').sort((a, b) => 
      new Date(b.endDate).getTime() - new Date(a.endDate).getTime()
    ), [seasons])
  
  const activeSeason = useMemo(() => 
    seasons.find(s => s.status === 'active'), [seasons])
  
  const currentSeason = selectedSeasonId 
    ? seasons.find(s => s.seasonId === selectedSeasonId)
    : activeSeason || sortedSeasons[0]
  
  const currentSeasonBrackets = currentSeason 
    ? seasonBrackets[currentSeason.seasonId] || []
    : []
  
  const selectedBracket = selectedBracketId
    ? currentSeasonBrackets.find(b => b.bracketId === selectedBracketId)
    : currentSeasonBrackets[0]
  
  const selectedTemplate = selectedBracket
    ? templates.find(t => t.templateId === selectedBracket.templateId) ?? null
    : null
  
  const currentPlayers = selectedBracket
    ? bracketPlayers[selectedBracket.bracketId] || []
    : []
  
  // Load league
  useEffect(() => {
    if (!groupId || !leagueId || !group) return
    
    let cancelled = false
    
    async function loadLeague() {
      setLeagueLoading(true)
      setLeagueError(null)
      try {
        const leagues = isMember 
          ? await leagueService.list(groupId!)
          : await leagueService.listPublic(groupId!)
        const leagueData = leagues.find(l => l.leagueId === leagueId)
        if (!cancelled) {
          if (leagueData) {
            setLeague(leagueData)
          } else {
            setLeagueError('League not found')
          }
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load league:', err)
          setLeagueError('Failed to load league')
        }
      } finally {
        if (!cancelled) {
          setLeagueLoading(false)
        }
      }
    }
    
    loadLeague()
    
    return () => { cancelled = true }
  }, [groupId, leagueId, group?.id, isMember])
  
  // Load seasons and templates
  useEffect(() => {
    if (!groupId || !leagueId || !group) return
    
    let cancelled = false
    
    async function loadData() {
      setSeasonsLoading(true)
      try {
        const [seasonsData, templatesData] = await Promise.all([
          isMember 
            ? seasonService.list(leagueId!)
            : seasonService.listPublic(leagueId!),
          isMember
            ? bracketTemplateService.list(leagueId!)
            : bracketTemplateService.listPublic(leagueId!),
        ])
        if (cancelled) return
        
        setSeasons(seasonsData)
        setTemplates(templatesData)
        
        const bracketsMap: Record<string, SeasonBracket[]> = {}
        await Promise.all(
          seasonsData.map(async (season) => {
            try {
              const brackets = isMember
                ? await bracketService.list(leagueId!, season.seasonId)
                : await bracketService.listPublic(leagueId!, season.seasonId)
              bracketsMap[season.seasonId] = brackets
            } catch (err) {
              console.error(`Failed to load brackets for season ${season.seasonId}:`, err)
              bracketsMap[season.seasonId] = []
            }
          })
        )
        if (!cancelled) {
          setSeasonBrackets(bracketsMap)
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load seasons/templates:', err)
        }
      } finally {
        if (!cancelled) {
          setSeasonsLoading(false)
        }
      }
    }
    
    loadData()
    
    return () => { cancelled = true }
  }, [groupId, leagueId, group?.id, isMember])

  // Check registration status for upcoming season with signups
  const upcomingSeasonWithSignups = useMemo(
    () => seasons.filter(s => s.status === 'upcoming').find(s => s.signupsOpen),
    [seasons]
  )

  useEffect(() => {
    if (!leagueId || !upcomingSeasonWithSignups || !isAuthenticated || !profile) {
      setMyRegistration(null)
      setIsAlreadyInSeason(false)
      return
    }

    async function checkRegistrationAndMembership() {
      setRegistrationLoading(true)
      try {
        const registration = await registrationService.getMyRegistration(
          leagueId!,
          upcomingSeasonWithSignups!.seasonId
        )
        setMyRegistration(registration)

        const brackets = seasonBrackets[upcomingSeasonWithSignups!.seasonId] || []
        let alreadyIn = false
        for (const bracket of brackets) {
          try {
            const players = isMember
              ? await bracketPlayerService.list(bracket.bracketId)
              : await bracketPlayerService.listPublic(bracket.bracketId)
            if (players.some(p => p.userId === profile!.userId)) {
              alreadyIn = true
              break
            }
          } catch {
            // Ignore errors for individual brackets
          }
        }
        setIsAlreadyInSeason(alreadyIn)
      } catch (err) {
        console.error('Failed to check registration:', err)
        setMyRegistration(null)
        setIsAlreadyInSeason(false)
      } finally {
        setRegistrationLoading(false)
      }
    }

    checkRegistrationAndMembership()
  }, [leagueId, upcomingSeasonWithSignups?.seasonId, isAuthenticated, profile?.userId, seasonBrackets, isMember])
  
  // Load players for selected bracket
  useEffect(() => {
    if (!groupId || !leagueId || !currentSeason || !selectedBracket || !group) return
    
    if (bracketPlayers[selectedBracket.bracketId]) return
    
    async function loadPlayers() {
      setPlayersLoading(true)
      try {
        const players = isMember
          ? await bracketPlayerService.list(selectedBracket!.bracketId)
          : await bracketPlayerService.listPublic(selectedBracket!.bracketId)
        setBracketPlayers(prev => ({
          ...prev,
          [selectedBracket!.bracketId]: players,
        }))
      } catch (err) {
        console.error('Failed to load players:', err)
      } finally {
        setPlayersLoading(false)
      }
    }
    
    loadPlayers()
  }, [groupId, leagueId, currentSeason, selectedBracket, bracketPlayers, isMember, group?.id])
  
  // Handler for when a match is recorded
  const handleMatchRecorded = () => {
    // Could refetch standings/players here in the future
  }

  // Get brackets for the upcoming signup season
  const upcomingSeasonBrackets = upcomingSeasonWithSignups
    ? seasonBrackets[upcomingSeasonWithSignups.seasonId] || []
    : []

  // Open registration dialog
  const handleOpenRegistration = () => {
    if (!isAuthenticated) {
      loginWithRedirect({ appState: { returnTo: window.location.pathname + window.location.search } })
      return
    }
    setRegistrationError(null)
    setShowRegistrationDialog(true)
  }

  // Submit registration
  const handleSubmitRegistration = async (bracketId: string) => {
    if (!leagueId || !upcomingSeasonWithSignups) return

    setRegistrationSubmitting(true)
    setRegistrationError(null)

    try {
      const registration = await registrationService.register(
        leagueId,
        upcomingSeasonWithSignups.seasonId,
        bracketId
      )
      setMyRegistration(registration)
      setShowRegistrationDialog(false)
    } catch (err) {
      setRegistrationError((err as Error)?.message || 'Failed to register')
    } finally {
      setRegistrationSubmitting(false)
    }
  }

  // Cancel registration
  const handleCancelRegistration = async () => {
    if (!leagueId || !upcomingSeasonWithSignups || !myRegistration) return

    const confirmed = window.confirm('Are you sure you want to cancel your registration?')
    if (!confirmed) return

    setRegistrationSubmitting(true)
    try {
      await registrationService.cancel(
        leagueId,
        upcomingSeasonWithSignups.seasonId,
        myRegistration.registrationId
      )
      setMyRegistration(null)
    } catch (err) {
      console.error('Failed to cancel registration:', err)
    } finally {
      setRegistrationSubmitting(false)
    }
  }

  // Update URL when season/bracket changes
  const setSelectedSeason = (seasonId: string) => {
    const brackets = seasonBrackets[seasonId] || []
    const firstBracket = brackets[0]
    
    setSearchParams({
      season: seasonId,
      ...(firstBracket ? { bracket: firstBracket.bracketId } : {}),
    })
    setSeasonDropdownOpen(false)
  }
  
  const setSelectedBracket = (bracketId: string) => {
    if (currentSeason) {
      setSearchParams({
        season: currentSeason.seasonId,
        bracket: bracketId,
      })
    }
  }
  
  // Loading state
  const isLoading = authLoading || (isAuthenticated && groupsLoading) || publicGroupLoading || leagueLoading || seasonsLoading
  
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    )
  }
  
  // Error state - group not accessible
  if (!group && publicGroupError) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <Card className="p-8 text-center">
            <Lock className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h2 className="text-xl font-semibold mb-2">Group not accessible</h2>
            <p className="text-muted-foreground mb-4">
              {publicGroupError === 'This group is not public' 
                ? "This group is private. You need to be a member to view it."
                : "This group doesn't exist or is not publicly accessible."
              }
            </p>
            {!isAuthenticated ? (
              <div className="flex flex-col items-center gap-3">
                <p className="text-sm text-muted-foreground">
                  If you're a member, please sign in to access this group.
                </p>
                <Button onClick={() => loginWithRedirect({ appState: { returnTo: `/group/${groupId}/league/${leagueId}` } })}>
                  Sign In
                </Button>
              </div>
            ) : (
              <Button onClick={() => navigate('/groups')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to My Groups
              </Button>
            )}
          </Card>
        </main>
      </div>
    )
  }
  
  // Error state - league not found
  if (leagueError || !league || !group) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <Card className="p-8 text-center">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h2 className="text-xl font-semibold mb-2">League not found</h2>
            <p className="text-muted-foreground mb-4">
              This league doesn't exist or you don't have access to it.
            </p>
            <Button onClick={() => navigate(`/group/${groupId}`)}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Group
            </Button>
          </Card>
        </main>
      </div>
    )
  }
  
  // Calculate days remaining for active season
  const daysRemaining = currentSeason && currentSeason.status === 'active' && currentSeason.endDate
    ? Math.max(0, Math.ceil((new Date(currentSeason.endDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : null

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {isPublicView && <VisitorBanner type="league" groupId={groupId} isPublicGroup={group?.isPublic} allowJoinRequests={group?.allowJoinRequests} />}
      
      <main className="container mx-auto px-4 py-8">
        {/* Back button and header */}
        <div className="mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/group/${groupId}`)} className="mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to {group.name}
          </Button>
          
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3 sm:gap-4 min-w-0">
              <div className="h-12 w-12 sm:h-16 sm:w-16 rounded-xl bg-primary/10 flex items-center justify-center overflow-hidden shrink-0">
                {group.icon ? (
                  <img src={group.icon} alt={group.name} className="h-full w-full object-cover" />
                ) : (
                  <Trophy className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <h1 className="text-xl sm:text-2xl font-bold truncate">{league.name}</h1>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground mt-1">
                  <span className="flex items-center gap-1">
                    {league.format === 'singles' ? (
                      <User className="h-3.5 w-3.5" />
                    ) : (
                      <UsersRound className="h-3.5 w-3.5" />
                    )}
                    {league.format === 'singles' ? 'Singles' : 'Doubles'}
                  </span>
                  <span className="flex items-center gap-1">
                    <Users className="h-3.5 w-3.5" />
                    {currentPlayers.length} players
                  </span>
                  {currentSeasonBrackets.length > 0 && (
                    <span className="hidden sm:flex items-center gap-1">
                      <Trophy className="h-3.5 w-3.5" />
                      {currentSeasonBrackets.length} bracket{currentSeasonBrackets.length !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              </div>
            </div>
            
            {canManageGroup && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon" className="shrink-0">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => navigate(`/group/${groupId}/admin/league/${leagueId}`, { state: { from: 'league-view' } })}>
                    <Settings className="h-4 w-4 mr-2" />
                    Manage League
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>

        {/* Upcoming Season Callout */}
        {upcomingSeasonWithSignups && (
          <UpcomingSeasonCallout
            season={upcomingSeasonWithSignups}
            brackets={upcomingSeasonBrackets}
            myRegistration={myRegistration}
            isAlreadyInSeason={isAlreadyInSeason}
            registrationLoading={registrationLoading}
            registrationSubmitting={registrationSubmitting}
            isMember={isMember}
            onOpenRegistration={handleOpenRegistration}
            onCancelRegistration={handleCancelRegistration}
          />
        )}

        {/* Season Selector */}
        <SeasonSelector
          seasons={seasons}
          activeSeason={activeSeason}
          upcomingSeasons={upcomingSeasons}
          pastSeasons={pastSeasons}
          currentSeason={currentSeason}
          onSeasonSelect={setSelectedSeason}
          dropdownOpen={seasonDropdownOpen}
          onDropdownOpenChange={setSeasonDropdownOpen}
          canManageGroup={!!canManageGroup}
          onManageLeague={() => navigate(`/group/${groupId}/admin/league/${leagueId}`, { state: { from: 'league-view' } })}
        />

        {/* Current Season Content */}
        {currentSeason && (
          <div className="space-y-6">
            {/* Bracket Selector & Record Game */}
            {currentSeasonBrackets.length > 0 && (
              <BracketSelector
                brackets={currentSeasonBrackets}
                templates={templates}
                selectedBracket={selectedBracket}
                selectedTemplate={selectedTemplate}
                onBracketSelect={setSelectedBracket}
                isMember={isMember}
                isSeasonActive={currentSeason.status === 'active'}
                players={currentPlayers}
                onRecordGame={() => setShowRecordGame(true)}
              />
            )}

            {/* Standings */}
            {selectedBracket && selectedTemplate && (
              <StandingsTable
                players={currentPlayers}
                playersLoading={playersLoading}
                template={selectedTemplate}
              />
            )}

            {/* No brackets message */}
            {currentSeasonBrackets.length === 0 && (
              <NoBracketsCard
                canManageGroup={!!canManageGroup}
                onActivateBrackets={() => navigate(`/group/${groupId}/admin/league/${leagueId}#season/${currentSeason.seasonId}`, { state: { from: 'league-view' } })}
              />
            )}

            {/* Season Info */}
            <SeasonInfoCard
              season={currentSeason}
              daysRemaining={daysRemaining}
            />
          </div>
        )}
        
        {/* Record Game Dialog */}
        {currentSeason && selectedBracket && league && (
          <RecordGameDialog
            open={showRecordGame}
            onOpenChange={setShowRecordGame}
            bracketId={selectedBracket.bracketId}
            players={currentPlayers}
            currentUserId={profile?.userId}
            seasonStatus={currentSeason.status}
            seasonName={currentSeason.name}
            matchFormat={league.matchFormat}
            autoApprove={league.autoApprove}
            maxGamesPerOpponent={currentSeason.maxGamesPerOpponent}
            onSuccess={handleMatchRecorded}
          />
        )}

        {/* Registration Dialog */}
        {upcomingSeasonWithSignups && (
          <RegisterInterestDialog
            open={showRegistrationDialog}
            onOpenChange={setShowRegistrationDialog}
            seasonName={upcomingSeasonWithSignups.name}
            seasonStartDate={upcomingSeasonWithSignups.startDate}
            seasonEndDate={upcomingSeasonWithSignups.endDate}
            joiningFee={upcomingSeasonWithSignups.joiningFee}
            termsAndConditions={upcomingSeasonWithSignups.termsAndConditions}
            bankDetails={group?.bankDetails}
            showBankDetails={upcomingSeasonWithSignups.showBankDetails}
            paymentReference={upcomingSeasonWithSignups.paymentReference}
            brackets={upcomingSeasonBrackets.map(b => ({ bracketId: b.bracketId, name: b.name }))}
            onSubmit={handleSubmitRegistration}
            submitting={registrationSubmitting}
            error={registrationError}
          />
        )}
      </main>
    </div>
  )
}
